// ============================================================================
// ContractGenerator.js — v1.0 — Passo 38 MC1
// Geração de PDF de Honorários Advocatícios com jsPDF (lazy-load)
// Retorna Blob pronto para upload no Google Drive.
// ============================================================================

// ── Helpers ─────────────────────────────────────────────────────────────────
function formatBRL(amount) {
  return Number(amount || 0).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(String(iso).includes('T') ? iso : iso + 'T00:00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return String(iso); }
}

function today() {
  return fmtDate(new Date().toISOString().slice(0, 10));
}

// ── generateHonorariumPDF ────────────────────────────────────────────────────
// Returns: Promise<Blob>
export async function generateHonorariumPDF({ clientName, clientId, processLabel, npu, amount, dueDate, description }) {
  const { jsPDF } = await import('jspdf');

  const BRAND_DARK  = [32, 36, 59];    // #20243B
  const BRAND_GOLD  = [197, 160, 89];  // #C5A059
  const GRAY_MID    = [100, 100, 110];
  const GRAY_LIGHT  = [230, 230, 234];

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W   = 210;
  const M   = 18;  // margin

  // ── Header Block ──────────────────────────────────────────────────────────
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, W, 38, 'F');

  doc.setTextColor(...BRAND_GOLD);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text('RODRIGO DEMETERCO ADVOCACIA', M, 14);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text('OAB/PR n\u00ba 113.938  \u2022  rodrigo@rodrigodemeterco.adv.br', M, 21);
  doc.text('Curitiba/PR  \u2022  \u00c1rea de Atua\u00e7\u00e3o: Direito Cível, Previdenciário e Trabalhista', M, 27);

  // ── Gold Rule ─────────────────────────────────────────────────────────────
  doc.setDrawColor(...BRAND_GOLD);
  doc.setLineWidth(0.8);
  doc.line(M, 33, W - M, 33);

  // ── Document Title ────────────────────────────────────────────────────────
  doc.setTextColor(...BRAND_DARK);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('CONTRATO / RECIBO DE HONOR\u00c1RIOS ADVOCAT\u00cdCIOS', W / 2, 48, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_MID);
  doc.text(`Emitido em: ${today()}`, W / 2, 54, { align: 'center' });

  // ── Section helper ────────────────────────────────────────────────────────
  let y = 64;
  const sectionTitle = (title) => {
    doc.setFillColor(...GRAY_LIGHT);
    doc.rect(M, y - 4, W - M * 2, 7, 'F');
    doc.setTextColor(...BRAND_DARK);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text(title.toUpperCase(), M + 2, y + 0.5);
    y += 10;
  };

  const row = (label, value) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GRAY_MID);
    doc.text(`${label}:`, M, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_DARK);
    const lines = doc.splitTextToSize(String(value || '\u2014'), W - M * 2 - 42);
    doc.text(lines, M + 42, y);
    y += Math.max(6, lines.length * 5.5);
  };

  // ── 1. Identificação do Contratante ────────────────────────────────────────
  sectionTitle('1. Identifica\u00e7\u00e3o do Contratante');
  row('Nome / Raz\u00e3o Social', clientName || '\u2014');
  row('ID / CPF', clientId   || '\u2014');
  y += 3;

  // ── 2. Objeto ──────────────────────────────────────────────────────────────
  sectionTitle('2. Objeto do Servi\u00e7o');
  row('Processo / Dossi\u00ea', processLabel || '\u2014');
  row('N\u00famero \u00danico (NPU)', npu         || 'N\u00e3o informado');
  row('Descri\u00e7\u00e3o', description || 'Presta\u00e7\u00e3o de servi\u00e7os advocat\u00edcios');
  y += 3;

  // ── 3. Honorários ──────────────────────────────────────────────────────────
  sectionTitle('3. Honor\u00e1rios e Condi\u00e7\u00f5es de Pagamento');

  doc.setFillColor(...BRAND_DARK);
  doc.roundedRect(M, y - 2, W - M * 2, 18, 3, 3, 'F');
  doc.setTextColor(...BRAND_GOLD);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('VALOR TOTAL DOS HONOR\u00c1RIOS', W / 2, y + 4, { align: 'center' });
  doc.setFontSize(16);
  doc.text(formatBRL(amount), W / 2, y + 12, { align: 'center' });
  y += 24;

  row('Vencimento', fmtDate(dueDate));
  row('Status', 'PENDENTE DE PAGAMENTO');
  y += 5;

  // ── 4. Cláusulas ──────────────────────────────────────────────────────────
  sectionTitle('4. Cl\u00e1usulas Essenciais');
  const clauses = [
    '4.1 O presente instrumento formaliza a presta\u00e7\u00e3o de servi\u00e7os advocat\u00edcios conforme as disposições do Estatuto da OAB (Lei n\u00ba 8.906/1994).',
    '4.2 O valor acordado é fixo e independente do resultado da demanda, salvo cláusula de \u00eaxito expressamente convencionada.',
    '4.3 A assinatura deste documento pelo Contratante implica plena ci\u00eancia e aceite das condições ora estabelecidas.',
  ];
  clauses.forEach((c) => {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY_MID);
    const lines = doc.splitTextToSize(c, W - M * 2);
    doc.text(lines, M, y);
    y += lines.length * 5 + 2;
  });
  y += 6;

  // ── 5. Assinaturas ─────────────────────────────────────────────────────────
  if (y > 240) { doc.addPage(); y = 20; }
  sectionTitle('5. Assinaturas');
  doc.setDrawColor(...BRAND_DARK);
  doc.setLineWidth(0.4);

  const sigLeft  = M;
  const sigRight = W / 2 + 8;
  const sigW     = (W - M * 2) / 2 - 6;
  const sigY     = y + 18;

  doc.line(sigLeft,  sigY, sigLeft  + sigW, sigY);
  doc.line(sigRight, sigY, sigRight + sigW, sigY);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_DARK);
  doc.text('Rodrigo Demeterco', sigLeft + sigW / 2, sigY + 5, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_MID);
  doc.text('Advogado — OAB/PR 113.938', sigLeft + sigW / 2, sigY + 9.5, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_DARK);
  doc.text(String(clientName || 'Contratante').slice(0, 38), sigRight + sigW / 2, sigY + 5, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_MID);
  doc.text('Contratante', sigRight + sigW / 2, sigY + 9.5, { align: 'center' });

  // ── Footer ─────────────────────────────────────────────────────────────────
  const footerY = 287;
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, footerY - 3, W, 12, 'F');
  doc.setFontSize(7.5);
  doc.setTextColor(...BRAND_GOLD);
  doc.text('Documento gerado eletronicamente pelo Sistema RDA — Portal Jur\u00eddico', W / 2, footerY + 2, { align: 'center' });
  doc.setTextColor(180, 180, 190);
  doc.text(`Emitido em ${today()} \u2022 Dado sigiloso — uso restrito`, W / 2, footerY + 6, { align: 'center' });

  return doc.output('blob');
}

// ── Filename helper ────────────────────────────────────────────────────────
export function buildHonorariumFilename(clientName) {
  const safe = String(clientName || 'Cliente')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '_')
    .slice(0, 40)
    .toUpperCase();
  const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `HONORARIOS_${safe}_${ts}.pdf`;
}
